/* eslint-disable @typescript-eslint/no-shadow */
import { CheckCircleOutline, InfoOutlined } from "@mui/icons-material";
import {
  Checkbox,
  Collapse,
  Container,
  FormControl,
  FormControlLabel,
  Link,
  MenuItem,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  DatePicker as MUIDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { addDays, addMonths, addWeeks, format } from "date-fns";
import { ROUTES } from "domain/configuration";
import unboundActions from "domain/core/actions";
import {
  getBillEstimate,
  getBillEstimateFetchStatus,
  getCurrentFormStep,
  getEvChargerInclusion,
  getExistingZAccount,
  getPartialSignUpDialogState,
  getSelectedAddress,
  getSelectedProduct,
  getSignupLitres,
  getUserDetails,
  getUserProperty,
  getUserRewards,
  getUserVerifiedDetail,
} from "domain/core/selectors";
import {
  DetailsInputSteps,
  DwellingType,
  FuelTypes,
  ProductName,
  SituationAtAddress,
  UsageType,
} from "domain/core/types";
import { media } from "lib/styles";
import { DATE_FORMAT } from "lib/types";
import {
  formatNumber,
  sendAnalyticsEvent,
  sendAnalyticsPageView,
} from "lib/util";
import moment from "moment";
import { useActions, useReduxState } from "re-reduced";
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { styled } from "styled-components";
import { BackButton } from "ui/components/BackButton";
import Button from "ui/components/Button";
import { SingleSelectRadio } from "ui/components/SingleSelectRadio";
import {
  ContentWrapper,
  ControlledCard,
  HeroTypography,
  RowLayoutLeft,
} from "ui/components/StyledComponents";
import UserDetailsForm from "ui/compounds/UserDetailsForm";
import { COLORS, PADDING } from "ui/theme";

const MAX_DATE = addMonths(new Date(), 3);
const MIN_SWITCH_DATE = addDays(new Date(), 1);
const MIN_MOVE_DATE = addWeeks(new Date(), -2);
const PLACEHOLDER_DATE = addWeeks(new Date(), 1);

const CardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${PADDING.small};
`;

const StyledInfo = styled(InfoOutlined)`
  margin-top: ${PADDING.xSmall};
  margin-right: ${PADDING.medium};
  color: ${COLORS.indigo};
`;

const StyledTick = styled(CheckCircleOutline)`
  margin-top: ${PADDING.xSmall};
  margin-right: ${PADDING.medium};
  color: ${COLORS.indigo};
`;

const SFormControlLabel = styled(FormControlLabel)`
  align-items: start;
`;

const IconRow = styled("div")`
  display: flex;
  margin: ${PADDING.medium} 0;
`;

interface State {
  fuelType?: FuelTypes;
  situation?: SituationAtAddress;
  medicallyDependent?: boolean;
  usageType?: UsageType;
  moveDate: Date | null;
  switchDate: Date | null;
  dwellingType?: DwellingType;
  errDate?: string;
  errSituation?: string;
  errMedical?: string;
  errDwellingType?: string;
  errUsageType?: string;
  errFuelType?: string;
}

const stateSelectorMap = {
  address: getSelectedAddress,
  existingZAccount: getExistingZAccount,
  verifiedUserDetail: getUserVerifiedDetail,
  rewards: getUserRewards,
  userProperty: getUserProperty,
  details: getUserDetails,
  campaignLitres: getSignupLitres,
  billEstimate: getBillEstimate,
  billEstimateFetchStatus: getBillEstimateFetchStatus,
  currentFormStep: getCurrentFormStep,
  partialSignUpDialogOpen: getPartialSignUpDialogState,
  selectedProduct: getSelectedProduct,
  includeEvCharger: getEvChargerInclusion,
};

const DatePicker = styled(MUIDatePicker)`
  display: flex;
  flex: 1;
  max-width: 100%;
  min-width: 10rem;
  ${media.md`
    max-width: 48%;
  `}
`;

export enum SectionHeaders {
  about_you = "Personal details",
  rewards = "Select your fuel",
  property = "Property details",
}

export const Details: React.FC = () => {
  window.onbeforeunload = () => "You have unsaved changes!";

  const actions = useActions(unboundActions);
  const reduxState = useReduxState(stateSelectorMap);

  // Section state handlers
  const [inputStep, setInputStep] = useState<DetailsInputSteps>(
    DetailsInputSteps.about_you,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formComplete, setFormComplete] = useState<boolean>(false);
  // Used to render conditional content for potentially unsupported property types.
  // We want to use the inclusion state at the point of this component rendering so that
  // toggling the input to remove it doesnt hide the itself.
  const [chargerIncluded] = useState<boolean>(reduxState.includeEvCharger);

  useEffect(() => {
    // get the current step name to ensure the correct form section is displayed
    const [_, currentFormStep] = reduxState.currentFormStep.split("step=");
    setInputStep(DetailsInputSteps[currentFormStep]);
  }, [reduxState.currentFormStep]);

  useEffect(() => {
    if (inputStep === DetailsInputSteps.none) return;
    sendAnalyticsPageView(SectionHeaders[inputStep]);
  }, [inputStep]);

  const handleOnVerify = () => {
    actions.updateFlickSignUp();
    actions.setCurrentFormStep(`/details?step=${DetailsInputSteps.rewards}`);
    setInputStep(DetailsInputSteps.rewards);
  };

  const handleOnContinue = () => {
    actions.updateFlickSignUp();
    actions.setCurrentFormStep(`/details?step=${DetailsInputSteps.property}`);
    setInputStep(DetailsInputSteps.property);
  };

  const handleOnRewardsContinue = () => {
    if (state.fuelType === undefined) {
      return setState((state) => ({
        ...state,
        errFuelType: "Oops, please select one.",
      }));
    }
    actions.setUserRewards({ fuelType: state.fuelType });
    actions.updateFlickSignUp();
    actions.setCurrentFormStep(`/details?step=${DetailsInputSteps.property}`);
    setInputStep(DetailsInputSteps.property);
  };

  const handlePropertyContinuePress = async () => {
    let moveDate;
    let switchDate;

    if (state.situation === undefined) {
      return setState((state) => ({
        ...state,
        errSituation: "Oops, please select one.",
      }));
    }

    if (!selectingMoveIn) {
      if (
        state.switchDate === null ||
        isNaN(state.switchDate?.getTime() ?? NaN) ||
        moment(state.switchDate).isBefore(moment(MIN_SWITCH_DATE), "day") ||
        moment(state.switchDate).isAfter(moment(MAX_DATE), "day")
      ) {
        return setState((state) => ({
          ...state,
          errDate: "Please select a valid date.",
        }));
      }
      switchDate = format(state.switchDate, DATE_FORMAT);
    } else {
      if (
        state.moveDate === null ||
        isNaN(state.moveDate?.getTime() ?? NaN) ||
        moment(state.moveDate).isBefore(moment(MIN_MOVE_DATE), "day") ||
        moment(state.moveDate).isAfter(moment(MAX_DATE), "day")
      ) {
        return setState((state) => ({
          ...state,
          errDate: "Please select a valid date.",
        }));
      }
      moveDate = format(state.moveDate, DATE_FORMAT);
    }

    if (
      reduxState.selectedProduct === ProductName.Z_EV &&
      !state.dwellingType
    ) {
      return setState((state) => ({
        ...state,
        errDwellingType: "Oops, please select one.",
      }));
    }

    if (state.medicallyDependent === undefined) {
      return setState((state) => ({
        ...state,
        errMedical: "Oops, please select one.",
      }));
    }

    if (state.usageType === undefined) {
      return setState((state) => ({
        ...state,
        errUsageType: "Oops, please select one.",
      }));
    }

    await setFormComplete(true);

    actions.setUserProperty({
      moveDate,
      switchDate,
      usageType: state.usageType,
      dwellingType: state.dwellingType,
      medicallyDependent: state.medicallyDependent,
      situation: state.situation,
    });
    await setInputStep(DetailsInputSteps.none);
    actions.updateFlickSignUp();
    // setting the next form step will also route the user to that form step
    actions.setCurrentFormStep("/payment");
  };

  // Form state handlers
  const [state, setState] = useState<State>({
    fuelType: undefined,
    situation: reduxState.userProperty.situation,
    dwellingType: reduxState.userProperty.dwellingType,
    medicallyDependent: reduxState.userProperty.medicallyDependent,
    moveDate:
      (reduxState.userProperty.moveDate &&
        new Date(reduxState.userProperty.moveDate)) ||
      null,
    switchDate:
      (reduxState.userProperty.switchDate &&
        new Date(reduxState.userProperty.switchDate)) ||
      null,
    usageType: undefined,
  });

  const handleFuelTypeChange = useCallback((fuelType: FuelTypes) => {
    setState((state) => ({ ...state, fuelType, errFuelType: undefined }));
  }, []);

  const handleSituationChange = useCallback((situation: SituationAtAddress) => {
    setState((state) => ({ ...state, situation, errSituation: "" }));
  }, []);

  const handleDwellingSelectChange = (dwellingValue: string) => {
    if ((Object.values(DwellingType) as string[]).includes(dwellingValue)) {
      setState((state) => ({
        ...state,
        dwellingType: dwellingValue as DwellingType,
        errDwellingType: "",
      }));
      sendAnalyticsEvent("component_interaction", {
        component_name: "Step 4 > Property type field",
        component_type: "form field",
        interaction_type: "select",
        interaction_value: dwellingValue,
      });
    }
  };

  const handleRemoveEvChargerInclusion = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      // input returns a true boolean when checked but the question is about removing
      // a charger add on so we need to invert the value before setting state.
      actions.setEvChargerInclusion(!checked);
    },
    [reduxState.includeEvCharger],
  );

  const handleUsageTypeChange = useCallback((usageType: UsageType) => {
    setState((state) => ({ ...state, usageType }));
  }, []);

  const handleMedicallyDepChange = useCallback((booleanString: string) => {
    const medicallyDependent = booleanString === "true";
    setState((state) => ({ ...state, medicallyDependent, errMedical: "" }));
  }, []);

  const showChargerAddOnWarning = useCallback(() => {
    return (
      (state.dwellingType === DwellingType.FLAT_OR_APARTMENT &&
        chargerIncluded) ||
      (state.dwellingType === DwellingType.TERRACE_HOUSE && chargerIncluded)
    );
  }, [state.dwellingType]);

  const handleMoveDateChange = useCallback((date: Date) => {
    setState((state) => ({ ...state, moveDate: date, errDate: "" }));
  }, []);

  const handleSwitchDateChange = useCallback((date: Date) => {
    setState((state) => ({ ...state, switchDate: date, errDate: "" }));
  }, []);

  const selectingMoveIn = state.situation === "moved_in";

  const renderSwitchDate = useCallback(() => {
    return (
      <Collapse in={state.situation === "switch_provider"}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Switch date"
            format="dd/MM/yyyy"
            maxDate={MAX_DATE}
            minDate={MIN_SWITCH_DATE}
            value={state.switchDate}
            onChange={handleSwitchDateChange}
            slotProps={{
              textField: {
                error: !!state.errDate,
                helperText: state.errDate,
                margin: "normal",
                inputProps: { id: "property-switch-date" },
              },
            }}
          />
        </LocalizationProvider>
      </Collapse>
    );
  }, [state]);

  const renderMoveDate = useCallback(() => {
    return (
      <Collapse in={state.situation === "moved_in"}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Move in date"
            format="dd/MM/yyyy"
            maxDate={MAX_DATE}
            minDate={MIN_MOVE_DATE}
            value={state.moveDate}
            onChange={handleMoveDateChange}
            slotProps={{
              textField: {
                error: !!state.errDate,
                helperText: state.errDate,
                margin: "normal",
                inputProps: { id: "property-move-in-date" },
              },
            }}
          />
        </LocalizationProvider>
      </Collapse>
    );
  }, [state]);

  const FUEL_TYPE_OPTIONS: Array<{
    label: string;
    value: FuelTypes;
    isVisible: boolean;
  }> = [
    { label: "Z91 Unleaded", value: "regular", isVisible: true },
    { label: "ZX95 Premium", value: "premium", isVisible: true },
    { label: "Z Diesel", value: "diesel", isVisible: true },
  ];

  const PROPERTY_SITUATION_OPTIONS: Array<{
    label: string;
    value: SituationAtAddress;
    isVisible: boolean;
    collapse?: ReactNode;
  }> = [
    {
      label: "I'm moving in, or have just moved in.",
      value: "moved_in",
      isVisible: true,
      collapse: renderMoveDate(),
    },
    {
      label: "I'd like to switch from another provider.",
      value: "switch_provider",
      isVisible: true,
      collapse: renderSwitchDate(),
    },
  ];

  const USAGE_TYPE_OPTIONS: Array<{
    label: string;
    value: UsageType;
    isVisible: boolean;
  }> = [
    {
      label: "Low user (typically spend less than $215 per month)",
      value: UsageType.LOW,
      isVisible: true,
    },
    {
      label: "Standard user (typically spend more than $215 per month)",
      value: UsageType.STANDARD,
      isVisible: true,
    },
  ];

  const MEDICALLY_DEPENDENT_OPTIONS: Array<{
    label: string;
    value: string;
    isVisible: boolean;
  }> = [
    {
      label: "Yes",
      value: "true",
      isVisible: true,
    },
    {
      label: "No",
      value: "false",
      isVisible: true,
    },
  ];

  return (
    <>
      <Container component="main">
        <ContentWrapper>
          <Collapse
            in={inputStep === DetailsInputSteps.about_you}
            mountOnEnter
            unmountOnExit
            addEndListener={() => {
              window.scrollTo(0, 0);
            }}
          >
            <Typography variant="body2" component="p">
              Step 3 of{" "}
              {reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS
                ? "6"
                : "5"}
            </Typography>
            <BackButton formStep={ROUTES.planDetails} />
            <Typography variant="h2" id="text-personal-details">
              {SectionHeaders[DetailsInputSteps.about_you]}
            </Typography>
            <UserDetailsForm
              onUserVerified={
                reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS
                  ? handleOnVerify
                  : handleOnContinue
              }
            />
          </Collapse>
          {reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS && (
            <>
              <Collapse
                in={inputStep === DetailsInputSteps.rewards}
                mountOnEnter
                unmountOnExit
                addEndListener={() => {
                  window.scrollTo(0, 0);
                }}
              >
                <Typography variant="body2" component="p">
                  Step 4 of{" "}
                  {reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS
                    ? "6"
                    : "5"}
                </Typography>
                <BackButton formStep={`${ROUTES.details}?step=about_you`} />
                <Typography variant="h2">
                  {SectionHeaders[DetailsInputSteps.rewards]}
                </Typography>
                <Typography variant="h3" id="text-verification-rewards">
                  You're all set to get these awesome fuel rewards
                </Typography>
                <CardContainer>
                  <ControlledCard>
                    <Typography variant="h4">Fuel for joining*</Typography>
                    <HeroTypography>
                      {reduxState.campaignLitres}L
                    </HeroTypography>
                    <Typography variant="caption" component="p">
                      You'll get fuel back just for joining Z at home.
                    </Typography>
                  </ControlledCard>
                  <ControlledCard>
                    <Typography variant="h4">Ongoing fuel back</Typography>
                    <HeroTypography>$100 = 5L</HeroTypography>
                    <Typography variant="caption" component="p">
                      You'll earn 5 litres of fuel for every $100 spent with Z
                      at home.
                    </Typography>
                    <Link
                      href={`${process.env.REACT_APP_Z_WEBSITE_URL}power/fuel-back`}
                      target="_blank"
                      variant="caption"
                      sx={{
                        marginTop: PADDING.medium,
                        marginBottom: 0,
                        display: "block",
                      }}
                    >
                      Fuel back details
                    </Link>
                  </ControlledCard>
                </CardContainer>
                <IconRow>
                  <StyledInfo />
                  {!reduxState.existingZAccount ? (
                    <>
                      <Typography variant="caption" component="p">
                        Your fuel rewards are earned in Sharetank - a virtual
                        fuel tank, as part of Z App. You'll need to register for
                        Z App with the below verified mobile number and start a
                        Sharetank to redeem your fuel rewards.{" "}
                        <Link
                          variant="caption"
                          href={`${process.env.REACT_APP_Z_WEBSITE_URL}power/help`}
                          target="_blank"
                        >
                          You can visit our Help Centre for any questions.
                        </Link>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" component="p">
                        Your fuel rewards are earned in Sharetank - a virtual
                        fuel tank, as part of the Z App. Our records show that
                        you’ve already registered for Z App with the below
                        verified mobile number. All you need to do is keep an
                        eye on your Sharetank - your fuel rewards will be
                        deposited in 14 days.{" "}
                        <Link
                          variant="caption"
                          href={`${process.env.REACT_APP_Z_WEBSITE_URL}power/help`}
                          target="_blank"
                        >
                          You can visit our help pages for any questions.
                        </Link>
                      </Typography>
                    </>
                  )}
                </IconRow>
                <IconRow>
                  <StyledTick />
                  <Typography variant="caption">
                    Verified number&nbsp;
                  </Typography>
                  <Typography
                    variant="caption"
                    display="inline"
                    sx={{ fontWeight: "600" }}
                  >
                    {formatNumber(reduxState.verifiedUserDetail)}
                  </Typography>
                </IconRow>
                <form>
                  <Typography variant="h3">
                    Select the type of fuel reward you want to have deposited in
                    to your Sharetank
                  </Typography>
                  <RowLayoutLeft>
                    <SingleSelectRadio
                      options={FUEL_TYPE_OPTIONS}
                      onChangeOption={handleFuelTypeChange}
                      selectedOptionValue={reduxState.rewards.fuelType}
                    />
                  </RowLayoutLeft>
                  {state.errFuelType && (
                    <Typography color="error">{state.errFuelType}</Typography>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleOnRewardsContinue}
                    id="button-continue-fuel-rewards"
                  >
                    Continue
                  </Button>
                </form>
              </Collapse>
            </>
          )}
          <Collapse
            mountOnEnter
            unmountOnExit
            addEndListener={() => {
              window.scrollTo(0, 0);
            }}
            in={inputStep === "property"}
          >
            {reduxState.selectedProduct === ProductName.Z_ELECTRIC_REWARDS && (
              <>
                <Typography variant="body2" component="p">
                  Step 5 of 6
                </Typography>
                <BackButton formStep={`${ROUTES.details}?step=rewards`} />
              </>
            )}
            {reduxState.selectedProduct !== ProductName.Z_ELECTRIC_REWARDS && (
              <>
                <Typography variant="body2" component="p">
                  Step 4 of 5
                </Typography>
                <BackButton formStep={`${ROUTES.details}?step=about_you`} />
              </>
            )}
            <Typography variant="h2">
              {SectionHeaders[DetailsInputSteps.property]}
            </Typography>
            <form>
              <Typography variant="h3">
                What's your current power situation?
              </Typography>
              <SingleSelectRadio
                options={PROPERTY_SITUATION_OPTIONS}
                onChangeOption={handleSituationChange}
                selectedOptionValue={state.situation ?? ""}
              />
              {state.errSituation && (
                <Typography color="error">{state.errSituation}</Typography>
              )}{" "}
              {reduxState.selectedProduct === ProductName.Z_EV && (
                <>
                  <Typography variant="h3">
                    What's your property type?
                  </Typography>
                  <FormControl fullWidth sx={{ maxWidth: "500px" }}>
                    <TextField
                      value={state.dwellingType}
                      onChange={(e) =>
                        handleDwellingSelectChange(e.target.value)
                      }
                      select
                      label="Select property type"
                    >
                      <MenuItem value={DwellingType.SINGLE_DETACHED_HOUSE}>
                        Single-detached house
                      </MenuItem>
                      <MenuItem value={DwellingType.SEMI_DETACHED_HOUSE}>
                        Semi-detached house
                      </MenuItem>
                      <MenuItem value={DwellingType.TERRACE_HOUSE}>
                        Terrace house
                      </MenuItem>
                      <MenuItem value={DwellingType.FLAT_OR_APARTMENT}>
                        Flat or Apartment
                      </MenuItem>
                      <MenuItem value={DwellingType.FARMHOUSE_OR_HOMESTEAD}>
                        Farmhouse or Homestead
                      </MenuItem>
                    </TextField>
                    {state.errDwellingType && (
                      <Typography color="error">
                        {state.errDwellingType}
                      </Typography>
                    )}
                    {showChargerAddOnWarning() && (
                      <>
                        <Typography variant="body1" display="inline">
                          Some property types may not be suitable for an Evnex
                          EV Smart charger, including apartments, rental
                          properties, and some townhouses. Please be advised
                          that if our installers find your property is not
                          suitable for an Evnex EV Smart charger add on you will
                          still be signed up for our EV plan.
                        </Typography>
                        <FormControl>
                          <SFormControlLabel
                            control={
                              <Checkbox
                                id="checkbox-charger-addon"
                                onChange={handleRemoveEvChargerInclusion}
                                value="charger_addon_checkbox"
                                color="primary"
                              />
                            }
                            label="Remove Evnex Smart Charger from EV at home plan"
                          />
                        </FormControl>
                      </>
                    )}
                  </FormControl>
                </>
              )}
              <Typography variant="h3">
                What type of power user are you?
              </Typography>
              <Typography variant="body1">
                Typically low users spend under $215 a month on power bills
                using less than 8,000-9,000 kWh per year, depending on your
                region. EV owners who charge at home are typically standard
                power users. If you are unsure please check your recent power
                bill.
              </Typography>
              <SingleSelectRadio
                options={USAGE_TYPE_OPTIONS}
                onChangeOption={handleUsageTypeChange}
              />
              {state.errUsageType && (
                <Typography color="error">{state.errUsageType}</Typography>
              )}{" "}
              <Typography variant="h3">
                Is anyone at the address medically dependent on electricity?
              </Typography>
              <Typography variant="body1">
                A ‘medically dependent customer’ relies on electricity for
                critical medical support for example, an oxygen machine. If you
                or anyone living with you at your home is dependent on
                electricity for medical reasons, it is important for you to
                contact us to enable us to provide you the right level of
                support.
              </Typography>
              <SingleSelectRadio
                options={MEDICALLY_DEPENDENT_OPTIONS}
                onChangeOption={handleMedicallyDepChange}
                selectedOptionValue={String(state.medicallyDependent)}
              />
              {state.medicallyDependent && (
                <Typography variant="caption" component="p">
                  We'll be in touch.
                </Typography>
              )}
              {state.errMedical && (
                <Typography color="error">{state.errMedical}</Typography>
              )}
              <Button
                variant="contained"
                onClick={handlePropertyContinuePress}
                id="button-next-step-details"
              >
                Continue
              </Button>
            </form>
          </Collapse>
        </ContentWrapper>
      </Container>
    </>
  );
};
